import { Component } from 'react';
import styled from 'styled-components';
import { nanoid } from 'nanoid';

import { colors, effects } from '/css';
import Icon from '/components/ui/Icon';
import Error from '/components/ui/Error';
import mediaApi from '/api/media';
import FileList from '/components/ui/FileList';

class FileInput extends Component {
    constructor(props) {
        super(props);

        this.state = {
            files: props.files || [],
            error: null,
        };

        this.id = props.id || 'id-' + nanoid(9);
    }

    setFilesState(files) {
        this.setState({ files });
    }

    getUploadHandeler() {
        if (this.props.customerUpload) return mediaApi.customerUpload;
        if (this.props.documentUpload) return mediaApi.documentUpload;
        return mediaApi.upload;
    }

    getAcceptedFormats() {
        if (this.props.accept) return this.props.accept;
        if (this.props.documentUpload) return 'application/pdf, .k2o';
        return 'image/jpeg, image/jpg, image/png, image/heif, image/heic';
    }

    async onFileSelected(e) {
        const { files } = this.state;
        const { maxFiles } = this.props;

        const selection = e.target.files;

        let acceptedFiles = [];

        for (var i = 0; i < selection.length; i++) {
            acceptedFiles.push(selection[i]);
        }

        if (acceptedFiles.length + files.length > maxFiles)
            acceptedFiles = acceptedFiles.filter(
                (file, i) => i < maxFiles - files.length
            );

        // fix missing XML tags on SVGs exported from Figma...
        if (String(this.props.accept).includes('image/svg+xml')) {
            for (let i = 0; i < acceptedFiles.length; i++) {
                acceptedFiles[i] = await this.fixMissingXmlTagOnSvgImages(
                    acceptedFiles[i]
                );
            }
        }

        let newFiles = acceptedFiles.map((file) =>
            Object.assign(file, {
                preview: URL.createObjectURL(file),
                processing: true,
            })
        );

        this.setState({ files: [...files, ...newFiles] });

        this.uploadFiles(newFiles);
    }

    async fixMissingXmlTagOnSvgImages(file) {
        // if not svg file stop here
        if (file.type !== 'image/svg+xml') return file;

        // read file to get its contents
        const missingXmlTag = `<?xml version="1.0" encoding="UTF-8"?>`;
        let svgCode = await this.readAsText(file);

        // if svg code is not missing xml tag stop here
        if (svgCode.includes(missingXmlTag)) return file;

        // add missing tag
        svgCode = missingXmlTag + `\n` + svgCode;

        // recreate new file form updated svg code
        const blob = new Blob([svgCode], { type: 'image/svg+xml' });
        const fixedFile = new File([blob], 'fixed-' + file.name, {
            type: 'image/svg+xml',
        });

        return fixedFile;
    }

    async readAsText(file) {
        let text = await new Promise((resolve) => {
            let fileReader = new FileReader();
            fileReader.onload = (e) => resolve(fileReader.result);
            fileReader.readAsText(file);
        });
        return text;
    }

    uploadFiles(acceptedFiles) {
        this.setState({ error: null });

        const uploadHandeler = this.getUploadHandeler();

        acceptedFiles.forEach(async (fileToUpload, i) => {
            const { data, error } = await uploadHandeler(fileToUpload);

            if (data?.id) {
                const newFiles = this.state.files.map((file) => {
                    return file.name === data.originalFilename ? data : file;
                });
                this.setState({ files: newFiles }, () => {
                    if (this.props.onUpload)
                        this.props.onUpload(newFiles.filter((file) => file.id));
                });
            } else {
                this.setState(
                    {
                        error: error,
                        files: this.state.files.filter(
                            (file) => file.preview !== fileToUpload.preview
                        ),
                    },
                    () => {
                        if (this.props.onUpload)
                            this.props.onUpload(
                                this.state.files.filter((file) => file.id)
                            );
                    }
                );
            }
        });
    }

    removeFile(index) {
        this.setState(
            { files: this.state.files.filter((file, i) => i !== index) },
            () => {
                if (this.props.onUpload)
                    this.props.onUpload(
                        this.state.files.filter((file) => file.id)
                    );
            }
        );
    }

    render() {
        const { maxFiles, className, lightbox, label, accept } = this.props;
        const { files, error } = this.state;

        return (
            <Wrapper
                className={className}
                hasFileSelector={files.length < maxFiles}
            >
                {error && <Error>{error}</Error>}

                <FileList
                    files={files}
                    lightbox={this.props.documentUpload ? false : lightbox}
                    onRemove={this.removeFile.bind(this)}
                    onImageClick={(file) => {
                        if (this.props.documentUpload) {
                            window.open(String(file.url || file));
                        }
                    }}
                />

                {files.length < maxFiles && (
                    <AddFile>
                        <label htmlFor={this.id}>
                            {label || 'Last opp bilde'}
                        </label>
                        <input
                            id={this.id}
                            type="file"
                            onChange={this.onFileSelected.bind(this)}
                            accept={this.getAcceptedFormats()}
                            multiple
                        />
                        <Icon icon="arrow-left" fill={colors.purple} />
                    </AddFile>
                )}
            </Wrapper>
        );
    }
}

export default FileInput;

const Wrapper = styled.div`
    max-width: 650px;
    .title {
        font-size: 24px;
        font-family: 'gellix-bold';
        color: ${colors.black};
    }
    ${(props) =>
        !props.hasFileSelector &&
        `
        .file-item:last-child {
            margin-bottom: 0;
        }
    `}
`;

const AddFile = styled.div`
    display: flex;
    ${effects.shadow};
    align-items: center;
    justify-content: flex-end;
    border: 2px solid transparent;
    transition: all 0.2s ease-in-out;
    padding: 14px 16px;
    border-radius: 12px;
    min-height: 54px;
    position: relative;
    svg {
        transform: rotate(90deg);
    }
    input {
        position: absolute;
        z-index: -1;
        left: -1000px;
        font-size: 0;
        visibility: hidden;
    }
    label {
        line-height: 1;
        font-size: 17px;
        font-family: 'gellix-semi';
        color: ${colors.black};
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 10;
        cursor: pointer;
        display: flex;
        align-items: center;
        padding: 14px 16px;
    }
`;
